import React from 'react'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Check, Cross, Download, Pencil, Upload } from '@factorialco/f0-react/icons/app'
import {
  ApprovePurchaseRequestDocument,
  PurchaseRequestDetailDocument,
  PurchaseRequestFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { fromCents } from 'modules/core/lib/formatCurrencyUtils'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { UpdatePurchaseOrderModal } from 'modules/procurement/components/purchaseOrders/UpdateOrderModal'
import { PurchaseRequestForm } from 'modules/procurement/components/purchaseRequests/PurchaseRequestForm'
import DownloadDocumentModal from 'modules/procurement/components/purchaseRequests/PurchaseRequestDetail/DownloadDocumentModal'
import RequestDocumentModal from 'modules/procurement/components/purchaseRequests/PurchaseRequestDetail/RequestDocumentModal'
import RejectionModal from 'modules/procurement/components/purchaseRequests/RejectionModal'
import {
  getApprovalProcessStatus,
  getPurchaseRequestStatusLabel,
} from 'modules/procurement/components/purchaseRequests/SpendingRequestStatusLabel'

interface PurchaseRequestDetailHeaderProps {
  purchaseRequest: PurchaseRequestFragment
  canManagePurchaseRequests?: boolean
  canManagePurchaseOrder?: boolean
  onlyOwnRequests: boolean
  isWorkflowsEnabled?: boolean
}

const PurchaseRequestDetailHeader = ({
  purchaseRequest,
  canManagePurchaseRequests = false,
  canManagePurchaseOrder = false,
  onlyOwnRequests,
  isWorkflowsEnabled = false,
}: PurchaseRequestDetailHeaderProps) => {
  const { company, access, employee } = useCurrentSession()
  const [rejectionModalOpen, setRejectionModalOpen] = React.useState(false)
  const [editRequestModalOpen, setEditRequestModalOpen] = React.useState(false)
  const [uploadDocumentTaskModalOpen, setUploadDocumentTaskModalOpen] = React.useState(false)
  const [downloadDocumentTaskModalOpen, setDownloadDocumentTaskModalOpen] = React.useState(false)
  const documentTasks = purchaseRequest?.uploadDocumentTask ?? []

  const todoTasks = documentTasks.filter((task) => task.taskStatus === 'todo')
  const currentDocumentTask = todoTasks[0] ?? null
  const isCurrentDocumentTaskUploadTask = currentDocumentTask?.taskType === 'upload'
  const isCurrentDocumentTaskDownloadTask = currentDocumentTask?.taskType === 'download'

  const isCurrentUserAssignee = currentDocumentTask?.taskAssigneeIds?.includes(access.id) ?? false

  const canPerformTaskAction = !!currentDocumentTask && isCurrentUserAssignee

  const showUploadDocumentTaskActions = canPerformTaskAction && isCurrentDocumentTaskUploadTask
  const showDownloadDocumentTaskActions = canPerformTaskAction && isCurrentDocumentTaskDownloadTask
  const [approvePurchaseRequest] = useMutation(ApprovePurchaseRequestDocument, {
    extractorFn: (data) => data.procurementMutations.approveProcurementPurchaseRequest,
    refetchQueries: [PurchaseRequestDetailDocument],
  })

  const areChangesRequested = purchaseRequest?.status === 'changes_requested'
  const [isApprovalStatusUpdated, setIsApprovalStatusUpdated] = React.useState(false)
  const [pendingPurchaseOrderId, setPendingPurchaseOrderId] = React.useState<string | null>()

  const isPurchaseRequestOwner = purchaseRequest?.requesterEmployee?.id === employee.id
  const showRequestChangesActions = areChangesRequested && isPurchaseRequestOwner

  const closeModal = () => {
    setPendingPurchaseOrderId(undefined)
  }

  const currentStep = purchaseRequest.approval?.steps.find((step) =>
    step.stepApprovers.some((approver) => approver.status === 'pending')
  )
  const currentStepAccesses = currentStep?.stepApprovers?.flatMap((approver) => approver.access?.id)

  const isCurrentAccessCurrentApprover = currentStepAccesses?.includes(access.id)

  const isApprovable =
    purchaseRequest &&
    purchaseRequest.status !== 'approved' &&
    purchaseRequest.status !== 'rejected'

  const canReview = isApprovable && canManagePurchaseRequests && isCurrentAccessCurrentApprover

  const showApprovalActions =
    !isApprovalStatusUpdated && purchaseRequest && canReview && !onlyOwnRequests

  const handleApprove = async () => {
    const result = await approvePurchaseRequest({
      variables: { id: purchaseRequest.id, companyId: company.id },
      refetchQueries: [PurchaseRequestDetailDocument],
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('procurement.purchase_requests.detail.responses.approve.error'),
      })
    })

    if (handled.success) {
      setIsApprovalStatusUpdated(true)

      const purchaseRequestStatus = handled.data.purchaseRequest?.status

      let message
      let finalApprover

      switch (purchaseRequestStatus) {
        case 'rejected':
          message = i18n.t('procurement.purchase_requests.detail.responses.reject.success')
          break
        case 'approved':
          if (canManagePurchaseOrder && handled.data.purchaseRequest?.purchaseOrder) {
            setPendingPurchaseOrderId(String(handled.data.purchaseRequest?.purchaseOrder.id))
          } else {
            message = i18n.t(
              'procurement.purchase_requests.detail.responses.approve.success_approved'
            )
          }
          finalApprover = true
          break
        default:
          message = i18n.t('procurement.purchase_requests.detail.responses.approve.success_pending')
          finalApprover = false
      }

      if (message) {
        app.setFlash({
          type: 'correct',
          message: message,
        })
      }

      track(trackingEvents.procurement.approvePurchaseRequest, {
        from: 'sidepanel',
        description: purchaseRequest?.description,
        cost: fromCents(purchaseRequest?.cost.cents ?? 0),
        currency: purchaseRequest?.cost.currency,
        deadline: purchaseRequest?.deadline ? 'yes' : 'no',
        URL: purchaseRequest?.url ? 'yes' : 'no',
        additionalInformation: purchaseRequest?.additionalInformation,
        vendorDetailTaxId: purchaseRequest?.vendor?.taxId,
        attachments: purchaseRequest.files.length !== 0 ? 'yes' : 'no',
        finalApprover: finalApprover,
      })
    }
  }

  const metadata = [
    {
      label: i18n.t('procurement.purchase_requests.detail.info.request_date'),
      value: {
        type: 'date' as const,
        formattedDate: dateBackendDateTimeToUI(purchaseRequest.date),
      },
    },
    {
      label: i18n.t('procurement.purchase_requests.detail.info.deadline'),
      value: {
        type: 'date' as const,
        formattedDate: purchaseRequest.deadline
          ? dateBackendDateTimeToUI(purchaseRequest.deadline)
          : '-',
      },
    },
    {
      label: i18n.t('procurement.purchase_orders.detail.info.type'),
      value: {
        color: 'smoke' as const,
        label: purchaseRequest?.type?.name || '',
        type: 'dot-tag' as const,
      },
    },
  ]

  const computedStatus = getApprovalProcessStatus({
    status: purchaseRequest.status,
    approvalProcess: purchaseRequest.approvalProcess,
  })

  const statusLabel = getPurchaseRequestStatusLabel(computedStatus)

  const getSecondaryActions = ():
    | Array<{
        icon: typeof Cross | typeof Pencil
        label: string
        onClick: () => void
      }>
    | undefined => {
    if (showApprovalActions && !isWorkflowsEnabled) {
      return [
        {
          icon: Cross,
          label: i18n.t('procurement.purchase_requests.detail.actions.reject'),
          onClick: () => setRejectionModalOpen(true),
        },
      ]
    }

    return undefined
  }

  const getPrimaryAction = ():
    | {
        icon?: typeof Check | typeof Pencil
        label: string
        onClick: () => void
      }
    | undefined => {
    if (showApprovalActions && !isWorkflowsEnabled) {
      return {
        icon: Check,
        label: i18n.t('procurement.purchase_requests.detail.actions.approve'),
        onClick: () => handleApprove(),
      }
    }

    if (showRequestChangesActions) {
      return {
        icon: Pencil,
        label: i18n.t('procurement.purchase_requests.detail.actions.edit_request'),
        onClick: () => setEditRequestModalOpen(true),
      }
    }

    if (showUploadDocumentTaskActions && !isWorkflowsEnabled) {
      return {
        icon: Upload,
        label: i18n.t('procurement.purchase_requests.detail.actions.upload_document_task'),
        onClick: () => setUploadDocumentTaskModalOpen(true),
      }
    }

    if (showDownloadDocumentTaskActions && !isWorkflowsEnabled) {
      return {
        icon: Download,
        label: i18n.t('procurement.purchase_requests.detail.actions.download_document_task'),
        onClick: () => setDownloadDocumentTaskModalOpen(true),
      }
    }

    return undefined
  }

  return (
    <>
      <ResourceHeader
        title={purchaseRequest.description}
        status={{ text: statusLabel.label, label: statusLabel.label, variant: statusLabel.type }}
        metadata={metadata}
        primaryAction={getPrimaryAction()}
        secondaryActions={getSecondaryActions()}
      />

      {rejectionModalOpen && (
        <RejectionModal
          id={purchaseRequest.id}
          onClose={() => setRejectionModalOpen(false)}
          handleUpdateApprovalStatus={setIsApprovalStatusUpdated}
        />
      )}

      {uploadDocumentTaskModalOpen && (
        <RequestDocumentModal
          isOpen={uploadDocumentTaskModalOpen}
          onClose={() => setUploadDocumentTaskModalOpen(false)}
          uploadDocumentTask={currentDocumentTask}
        />
      )}

      {downloadDocumentTaskModalOpen && (
        <DownloadDocumentModal
          isOpen={downloadDocumentTaskModalOpen}
          onClose={() => setDownloadDocumentTaskModalOpen(false)}
          documentTask={currentDocumentTask}
        />
      )}

      {pendingPurchaseOrderId && (
        <UpdatePurchaseOrderModal
          purchaseOrderId={pendingPurchaseOrderId}
          handleClose={closeModal}
        />
      )}

      {editRequestModalOpen && purchaseRequest.type && (
        <PurchaseRequestForm
          procurementType={purchaseRequest.type}
          isOpen={editRequestModalOpen}
          onClose={() => setEditRequestModalOpen(false)}
          existingPurchaseRequest={{
            id: purchaseRequest.id,
            description: purchaseRequest.description,
            cost: purchaseRequest.cost,
            url: purchaseRequest.url,
            deadline: purchaseRequest.deadline,
            additionalInformation: purchaseRequest.additionalInformation,
            vendor: purchaseRequest.vendor ?? null,
            formsMembership: purchaseRequest.formsMembership
              ? {
                  answers:
                    purchaseRequest.formsMembership.answers?.map((answer) => ({
                      answer: answer.answer || '',
                      element: { id: answer.element?.id ?? '0' },
                    })) ?? null,
                }
              : null,
          }}
        />
      )}
    </>
  )
}

export default PurchaseRequestDetailHeader
